import React, { useState, useEffect } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { TrendingDown, Info, Download, AlertTriangle } from 'lucide-react';

const DecliningNAVPage = () => {
  const [amcList, setAmcList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [schemeData, setSchemeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAmc, setSelectedAmc] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'decline_percent', direction: 'asc' });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchSchemeData();
  }, [selectedAmc, selectedCategory]);

  const fetchInitialData = async () => {
    try {
      const [amcRes, catRes] = await Promise.all([
        fetch(`${API_URL}/api/research/amc-list`),
        fetch(`${API_URL}/api/research/scheme-categories`)
      ]);
      
      const amcData = await amcRes.json();
      const catData = await catRes.json();
      
      if (amcData.status === 'success') setAmcList(amcData.data);
      if (catData.status === 'success') setCategories(catData.data);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
  };

  const fetchSchemeData = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/research/declining-nav?amc=${selectedAmc}&category=${selectedCategory}`
      );
      const data = await res.json();
      if (data.status === 'success') {
        setSchemeData(data.data);
      }
    } catch (error) {
      console.error('Error fetching scheme data:', error);
    }
    setLoading(false);
  };

  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = [...schemeData].sort((a, b) => {
    const aVal = a[sortConfig.key] || 0;
    const bVal = b[sortConfig.key] || 0;
    return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const getDeclineColor = (value) => {
    const absVal = Math.abs(value);
    if (absVal >= 20) return 'text-red-500';
    if (absVal >= 15) return 'text-red-400';
    if (absVal >= 10) return 'text-orange-400';
    return 'text-yellow-400';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <Navbar />
      
      <div className="pt-28 pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl mb-4">
              <TrendingDown className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Most Declining NAV Analysis
            </h1>
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 max-w-3xl mx-auto">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-white/80 text-sm text-left">
                  This tool displays the change in NAV of schemes from their peak value, along with Sensex movement reference.
                  <strong> Decline of NAV from peak</strong> is a popular parameter for scheme analysis.
                </p>
              </div>
            </div>
          </div>

          {/* Warning Banner */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
            <p className="text-yellow-400 text-sm">
              Schemes with significant NAV decline may present buying opportunities or indicate underlying issues. Always conduct thorough research before investing.
            </p>
          </div>

          {/* Filters */}
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 mb-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-white/70 text-sm mb-2 block">Select Fund House</label>
                <select
                  value={selectedAmc}
                  onChange={(e) => setSelectedAmc(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-red-500 appearance-none cursor-pointer"
                >
                  <option value="all" className="bg-gray-900">All Fund Houses</option>
                  {amcList.map((amc, idx) => (
                    <option key={idx} value={amc} className="bg-gray-900">{amc}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-white/70 text-sm mb-2 block">Select Scheme Type</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-red-500 appearance-none cursor-pointer"
                >
                  {categories.map((cat, idx) => (
                    <option key={idx} value={cat.value} className="bg-gray-900">{cat.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Results Count */}
          <div className="flex justify-between items-center mb-4">
            <p className="text-white/60 text-sm">
              Showing <span className="text-white font-semibold">{sortedData.length}</span> schemes with declining NAV
            </p>
            <button className="flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 hover:bg-red-500/30 transition-colors text-sm">
              <Download className="w-4 h-4" />
              Export Data
            </button>
          </div>

          {/* Data Table */}
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-white/5 border-b border-white/10">
                    <th className="text-left py-4 px-4 text-white/70 font-medium text-sm">Scheme Name</th>
                    <th className="text-center py-4 px-3 text-white/70 font-medium text-sm">Date of Highest NAV</th>
                    <th className="text-right py-4 px-3 text-white/70 font-medium text-sm cursor-pointer hover:text-white" onClick={() => handleSort('sensex')}>
                      Sensex {sortConfig.key === 'sensex' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                    </th>
                    <th className="text-right py-4 px-3 text-white/70 font-medium text-sm cursor-pointer hover:text-white" onClick={() => handleSort('highest_nav')}>
                      Highest NAV {sortConfig.key === 'highest_nav' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                    </th>
                    <th className="text-right py-4 px-3 text-white/70 font-medium text-sm cursor-pointer hover:text-white" onClick={() => handleSort('current_nav')}>
                      Current NAV {sortConfig.key === 'current_nav' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                    </th>
                    <th className="text-right py-4 px-3 text-white/70 font-medium text-sm cursor-pointer hover:text-white" onClick={() => handleSort('decline_percent')}>
                      Decline (%) {sortConfig.key === 'decline_percent' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-white/50">
                        <div className="flex items-center justify-center gap-3">
                          <div className="w-6 h-6 border-2 border-red-500/30 border-t-red-500 rounded-full animate-spin"></div>
                          Loading declining NAV data...
                        </div>
                      </td>
                    </tr>
                  ) : sortedData.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-white/50">
                        No schemes found for the selected filters
                      </td>
                    </tr>
                  ) : (
                    sortedData.map((scheme, idx) => (
                      <tr key={idx} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="py-4 px-4">
                          <div>
                            <p className="text-white font-medium text-sm">{scheme.scheme_name}</p>
                            <p className="text-white/50 text-xs mt-1">{scheme.amc}</p>
                          </div>
                        </td>
                        <td className="text-center py-4 px-3 text-white/70 text-sm">{scheme.highest_nav_date}</td>
                        <td className="text-right py-4 px-3 text-white/70 font-mono text-sm">{scheme.sensex?.toLocaleString('en-IN')}</td>
                        <td className="text-right py-4 px-3 text-white font-mono text-sm">₹{scheme.highest_nav?.toFixed(2)}</td>
                        <td className="text-right py-4 px-3 text-white font-mono text-sm">₹{scheme.current_nav?.toFixed(2)}</td>
                        <td className={`text-right py-4 px-3 font-mono text-sm font-semibold ${getDeclineColor(scheme.decline_percent)}`}>
                          {scheme.decline_percent?.toFixed(2)}%
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-6 text-center">
            <p className="text-white/40 text-xs">
              * NAV decline is calculated from the all-time high NAV. This data is for informational purposes only and should not be considered investment advice.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DecliningNAVPage;
